import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ILocalization } from '@shared-ui/localization-context';
import { PropertyListingHeaderMessages } from '@shared-ui/lodging-property-listing';
import { SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { SearchCriteriaModel } from 'stores/models';
import { SearchResultsStore } from 'stores/search-results-store';

import {
  LodgingEGDSPlainTextFragment,
  LodgingMoreInfoTriggerFragment,
  LodgingPwaPropertySearch,
  SearchDialogMessage,
  SearchLinkMessage,
} from '__generated__/api/types';
import { LoyaltyMessagingModel } from 'models';

import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { SWPToggle } from 'components/common/swp-toggle';
import { UniversalSortFilterWrapper } from './sort-filter/universal-sort-filter';

export interface ResultsSubHeaderProps {
  resultsSummary?: (LodgingPwaPropertySearch.resultsSummary & SearchLinkMessage.fragment & SearchDialogMessage.fragment)[];
  resultMessages?: (LodgingPwaPropertySearch.resultMessages &
    LodgingEGDSPlainTextFragment.fragment &
    LodgingMoreInfoTriggerFragment.fragment)[];
  isLoading: boolean;
  l10n: ILocalization;
  loyaltyMessagingModel?: LoyaltyMessagingModel;
  searchCriteria: SearchCriteriaModel;
  searchResults?: SearchResultsStore;
  isQuickAccessFiltersActive: boolean;
}

export const ResultsSubHeader: React.FunctionComponent<ResultsSubHeaderProps> = inject('searchResults')(
  observer(props => {
    const {
      resultsSummary,
      resultMessages,
      isLoading,
      l10n,
      loyaltyMessagingModel,
      searchCriteria,
      searchResults,
      isQuickAccessFiltersActive,
    } = props;
    const isError = searchResults?.isError;
    const multiItem = searchCriteria && searchCriteria.misId;
    const lob = multiItem ? 'multiItem' : 'hotels';
    const localizedText = l10n.formatText(`${lob}.searchResults.loadingMessage.text`);

    const Loading = () => (
      <>
        {isLoading && (
          <UitkSpacing padding={{ blockend: 'two' }}>
            <UitkText inline={false} align="left" theme="default">
              {localizedText}
            </UitkText>
          </UitkSpacing>
        )}
      </>
    );

    return (
      <header>
        <Viewport>
          <ViewSmall>
            <>
              {!isError && (
                <>
                  {loyaltyMessagingModel && <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />}
                  <Loading />
                  {!isLoading && <HeaderMessages resultsSummary={resultsSummary} resultMessages={resultMessages} />}
                </>
              )}
            </>
          </ViewSmall>
          <ViewLarge>
            <UitkLayoutGrid columns={8}>
              <UitkLayoutGridItem colSpan={5}>
                <div>
                  {!isError && (
                    <>
                      {loyaltyMessagingModel && <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />}
                      <Loading />
                      {!isLoading && <HeaderMessages resultsSummary={resultsSummary} resultMessages={resultMessages} />}
                    </>
                  )}
                </div>
              </UitkLayoutGridItem>
              {!isQuickAccessFiltersActive && (
                <UitkLayoutGridItem colSpan={3}>
                  <div>
                    <UniversalSortFilterWrapper view={SortAndFiltersViewType.SORT} />
                  </div>
                </UitkLayoutGridItem>
              )}
            </UitkLayoutGrid>
          </ViewLarge>
        </Viewport>
      </header>
    );
  })
);
ResultsSubHeader.displayName = 'ResultsSubHeader';

export interface HeaderMessagesProps {
  resultsSummary?: (LodgingPwaPropertySearch.resultsSummary & SearchLinkMessage.fragment & SearchDialogMessage.fragment)[];
  resultMessages?: (LodgingPwaPropertySearch.resultMessages &
    LodgingEGDSPlainTextFragment.fragment &
    LodgingMoreInfoTriggerFragment.fragment)[];
}

export const HeaderMessages: React.FunctionComponent<HeaderMessagesProps> = props => {
  const { resultsSummary, resultMessages } = props;

  if (!resultsSummary || resultsSummary.length === 0) {
    return null;
  }

  return (
    <div data-stid="results-header-messages">
      <PropertyListingHeaderMessages inputs={{} as any} messages={resultMessages} />
    </div>
  );
};
HeaderMessages.displayName = 'HeaderMessages';
